import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import "../styles/GerenciamentoProfessores.css";
import { getStatusLabel } from "../lib/utils"; // Importar funções utilitárias
import professoresService from "../services/professores.service"; // Importação do serviço de professores
import { criarUsuarioComLogin } from "../services/criar-usuario.service"; // Edge Function A5
import aulasService from "../services/aulas.service"; // Importação do serviço de aulas como export padrão
import { alterarPapel } from "../services/alterar-papel.service"; // Edge Function alterar-papel
import supabase from "../config/supabaseConfig";
import { useAuth } from "../hooks/useAuth";
import { getViewAs } from "../lib/viewAs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClockRotateLeft,
  faPen,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

// Rótulo pt-BR de cada papel + o select oferecido pelo admin.
const PAPEL_LABEL = { professor: "Professor", lider: "Líder", admin: "Admin" };
const PAPEIS_SELECT = ["professor", "lider", "admin"];

// Quem pode adicionar professor COM LOGIN pela UI (#11). Recepção fora —
// lista extensível de propósito (desenho fechado 03/07).
const PODE_ADICIONAR_PROFESSOR = ["admin", "lider"];
// Quem pode EXCLUIR professor pela UI (pedido Rodrigo 22/07): recepção só
// edita, nunca exclui — mesma lista de quem pode criar o login.
const PODE_EXCLUIR_PROFESSOR = PODE_ADICIONAR_PROFESSOR;

// Iniciais para o avatar do professor (ex.: "Alex Souza" -> "AS")
const iniciais = (nome = "") =>
  nome
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase() || "?";

const GerenciamentoProfessores = ({ userRole }) => {
  const { user, isAdmin } = useAuth();
  // Respeita "Ver como" pro admin testar os outros papéis (padrão PresalaNova).
  const viewAs = isAdmin ? getViewAs(user?.email) : null;
  const papelEfetivo = viewAs?.role || user?.role;

  const [showModal, setShowModal] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [professorEditando, setProfessorEditando] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [professores, setProfessores] = useState([]);
  // Papel de cada professor (profiles.role), por id. Professor sem login não
  // tem entrada aqui — a coluna mostra "—" nesse caso.
  const [papeisPorProfessor, setPapeisPorProfessor] = useState(() => new Map());
  // id do professor cujo select está salvando (desabilita a linha).
  const [salvandoPapelId, setSalvandoPapelId] = useState(null);

  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");
  // nome + email + senha obrigatórios no cadastro — a Edge Function
  // criar-professor (A5) cria o login DE VERDADE; idade/especialidade/
  // experiência/formação seguem opcionais.
  const [novoProfessor, setNovoProfessor] = useState({
    nome: "",
    email: "",
    senha: "",
    idade: "",
    especialidade: "",
    experiencia: "",
    formacao: "",
  });
  const [professorHistorico, setProfessorHistorico] = useState(null);

  // Estado para o modal de detalhes da aula
  const [aulaDetalhes, setAulaDetalhes] = useState(null);
  const [showAulaDetalhes, setShowAulaDetalhes] = useState(false);
  const [historicoAulas, setHistoricoAulas] = useState([]);
  // Contador de aulas realizadas por professor (agregado no backend — regra §2 —
  // via RPC aulas_realizadas_por_professor; evita o teto de 1000 do getAll).
  const [aulasRealizadasPorProfessor, setAulasRealizadasPorProfessor] =
    useState(() => new Map());

  // Referência para o dropdown
  const dropdownRef = useRef({});

  // Carregar o contador de aulas realizadas por professor.
  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const { data, error } = await supabase.rpc(
          "aulas_realizadas_por_professor"
        );
        if (error) throw error;
        if (ativo) {
          setAulasRealizadasPorProfessor(
            new Map((data || []).map((r) => [r.professor_id, r.total]))
          );
        }
      } catch (err) {
        console.error("Erro ao carregar contador de aulas por professor:", err);
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  // Carregar professores do serviço ao montar o componente
  useEffect(() => {
    carregarProfessores();
  }, []);

  // Função para carregar os professores
  const carregarProfessores = async () => {
    try {
      const data = await professoresService.getAll();
      setProfessores(data);
      await carregarPapeis(data.map((p) => p.id));
    } catch (err) {
      console.error("Erro ao carregar professores:", err);
      alert("Erro ao carregar professores: " + err.message);
    }
  };

  // Papel de cada professor vem de profiles.role (profiles.id === professores.id).
  // Professor sem login não tem linha em profiles — fica de fora do Map.
  const carregarPapeis = async (ids) => {
    if (!ids || ids.length === 0) {
      setPapeisPorProfessor(new Map());
      return;
    }
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, role")
        .in("id", ids);
      if (error) throw error;
      setPapeisPorProfessor(new Map((data || []).map((p) => [p.id, p.role])));
    } catch (err) {
      console.error("Erro ao carregar papéis dos professores:", err);
    }
  };

  // Admin troca o papel de um professor. Confirma, chama a Edge Function,
  // atualiza o estado local em sucesso e reverte o select em erro.
  const handleTrocarPapel = async (professor, novoPapel) => {
    const papelAtual = papeisPorProfessor.get(professor.id);
    if (novoPapel === papelAtual) return;

    const confirmacao = window.confirm(
      `Mudar o papel de ${professor.nome} para ${PAPEL_LABEL[novoPapel]}?`
    );
    if (!confirmacao) return;

    setSalvandoPapelId(professor.id);
    try {
      const resultado = await alterarPapel({
        userId: professor.id,
        novoPapel,
      });
      setPapeisPorProfessor((prev) => {
        const novo = new Map(prev);
        novo.set(professor.id, resultado.papel);
        return novo;
      });
      toast.success(
        `Papel de ${professor.nome} alterado para ${PAPEL_LABEL[resultado.papel]}.`
      );
    } catch (err) {
      console.error("Erro ao alterar papel:", err);
      toast.error("Erro ao alterar papel: " + err.message);
      // reverte: nada foi alterado no estado, o select volta ao valor de papeisPorProfessor
    } finally {
      setSalvandoPapelId(null);
    }
  };

  // Carregar histórico de aulas
  useEffect(() => {
    carregarHistoricoAulas();
  }, []);

  // Função para carregar o histórico de aulas
  const carregarHistoricoAulas = async () => {
    try {
      // CORRIGIDO: Renomear a chamada da função para getAll
      const aulas = await aulasService.getAll();
      setHistoricoAulas(aulas);
    } catch (err) {
      console.error("Erro ao carregar histórico de aulas:", err);
    }
  };

  // Adicionar useEffect para fechar o dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Se temos um dropdown ativo e o clique não foi dentro do dropdown atual ou no botão
      if (
        activeDropdown !== null &&
        dropdownRef.current[activeDropdown] &&
        !dropdownRef.current[activeDropdown].contains(event.target)
      ) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeDropdown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNovoProfessor({
      ...novoProfessor,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (novoProfessor.nome.trim() === "" || novoProfessor.email.trim() === "")
      return;

    if (modoEdicao && professorEditando) {
      // Modo de edição - atualizar professor existente
      const professorAtualizado = {
        ...professorEditando,
        nome: novoProfessor.nome,
        email: novoProfessor.email || null,
        idade: novoProfessor.idade ? parseInt(novoProfessor.idade) : null,
        especialidade: novoProfessor.especialidade,
        experiencia: novoProfessor.experiencia,
        formacao: novoProfessor.formacao,
      };

      try {
        await professoresService.update(
          professorEditando.id,
          professorAtualizado
        );
        const professoresAtualizados = professores.map((professor) =>
          professor.id === professorEditando.id
            ? professorAtualizado
            : professor
        );
        setProfessores(professoresAtualizados);
      } catch (err) {
        console.error("Erro ao atualizar professor:", err);
        alert("Erro ao atualizar professor: " + err.message);
      }
    } else {
      // Modo de cadastro - a Edge Function criar-professor (A5) cria o login
      // no Auth + a linha em professores (id = auth user id) numa tacada só.
      if (novoProfessor.senha.length < 6) {
        toast.error("Senha provisória precisa de pelo menos 6 caracteres.");
        return;
      }

      try {
        await criarUsuarioComLogin({
          ...novoProfessor,
          papel: "professor",
        });
        // Recarrega do banco: a linha foi criada pela function, não localmente.
        await carregarProfessores();
        toast.success(
          `Professor ${novoProfessor.nome} criado com login ativo. A senha provisória deve ser trocada no primeiro acesso.`
        );
      } catch (err) {
        console.error("Erro ao criar professor:", err);
        toast.error("Erro ao criar professor: " + err.message);
        return; // mantém o modal aberto pra corrigir e tentar de novo
      }
    }

    // Resetar formulário e fechar modal
    setNovoProfessor({
      nome: "",
      email: "",
      senha: "",
      idade: "",
      especialidade: "",
      experiencia: "",
      formacao: "",
    });
    setModoEdicao(false);
    setProfessorEditando(null);
    setShowModal(false);
  };

  const handleDelete = async (professor) => {
    try {
      // Confirmar exclusão antes de prosseguir
      const confirmacao = window.confirm(
        `⚠️ ATENÇÃO: Você está prestes a EXCLUIR PERMANENTEMENTE o professor "${professor.nome}".\n\n` +
          `Esta ação é IRREVERSÍVEL e o professor será removido completamente do sistema.\n\n` +
          `Deseja continuar?`
      );

      if (!confirmacao) {
        setActiveDropdown(null);
        return;
      }

      // Excluir professor completamente do sistema
      const resultado = await professoresService.deleteComplete(professor.id);

      if (resultado.success) {
        // Atualizar lista de professores
        await carregarProfessores();

        // Mostrar mensagem de sucesso
        alert(
          `✅ Professor ${professor.nome} foi excluído permanentemente do sistema.`
        );
      } else {
        throw new Error(resultado.message || "Erro desconhecido");
      }

      setActiveDropdown(null);
    } catch (err) {
      console.error("Erro ao excluir professor:", err);
      alert(`❌ Erro ao excluir professor: ${err.message}`);
      setActiveDropdown(null);
    }
  };

  const handleVerHistorico = (professor) => {
    setProfessorHistorico(professor);
    setActiveDropdown(null); // Fecha o dropdown ao abrir o modal
  };

  // Função para abrir o modal de detalhes da aula
  const verDetalhesAula = (aulaId) => {
    // Buscar a aula completa no histórico geral
    const aulaCompleta = historicoAulas.find((aula) => aula.id === aulaId);
    if (aulaCompleta) {
      setAulaDetalhes(aulaCompleta);
      setShowAulaDetalhes(true);
      setActiveDropdown(null); // Fecha o dropdown ao abrir o modal
    } else {
      alert("Detalhes da aula não encontrados.");
    }
  };

  // Função para fechar o modal de detalhes
  const fecharDetalhesAula = () => {
    setShowAulaDetalhes(false);
    setAulaDetalhes(null);
  };

  const filteredItems = professores.filter((item) =>
    item.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );


  const handleEdit = (professor) => {
    setNovoProfessor({
      nome: professor.nome,
      email: professor.email || "",
      senha: "",
      idade: (professor.idade ?? "").toString(),
      especialidade: professor.especialidade || "",
      experiencia: professor.experiencia || "",
      formacao: professor.formacao || "",
    });
    setProfessorEditando(professor);
    setModoEdicao(true);
    setShowModal(true);
    setActiveDropdown(null); // Fecha o dropdown ao abrir o modal
  };

  const openModal = () => {
    setNovoProfessor({
      nome: "",
      email: "",
      senha: "",
      idade: "",
      especialidade: "",
      experiencia: "",
      formacao: "",
    });
    setModoEdicao(false);
    setProfessorEditando(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModoEdicao(false);
    setProfessorEditando(null);
  };

  // Fechar dropdown quando clicar fora dele
  const closeDropdown = (e) => {
    if (e) e.stopPropagation();
    setActiveDropdown(null);
  };

  return (
    <div className="professores-principal-container">
      <h1>Gerenciamento de Professores</h1>

      <div className="actions-container">
        {PODE_ADICIONAR_PROFESSOR.includes(userRole) && (
          <button className="btn-cadastrar" onClick={openModal}>
            <span>+</span> Adicionar Professor
          </button>
        )}

        <div className="list-controls">
          <div className="show-entries">
            <span>Mostrar</span>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>registros</span>
          </div>

          <div className="search-box">
            <span>Buscar:</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Nome do professor"
            />
          </div>
        </div>
      </div>

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>PROFESSOR</th>
              <th>IDADE</th>
              <th>ESPECIALIDADE</th>
              <th>EXPERIÊNCIA</th>
              <th>AULAS REALIZADAS</th>
              <th>PAPEL</th>
              <th>AÇÕES</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.slice(0, itemsPerPage).map((item) => (
              <tr key={item.id}>
                <td>
                  <div className="prof-cel">
                    <span className="prof-av">{iniciais(item.nome)}</span>
                    <span>{item.nome}</span>
                  </div>
                </td>
                <td>
                  {item.idade !== null && item.idade !== undefined
                    ? item.idade
                    : "—"}
                </td>
                <td>{item.especialidade || "—"}</td>
                <td>{item.experiencia || "—"}</td>
                <td>
                  <span className="badge-aulas">
                    {aulasRealizadasPorProfessor.get(item.id) || 0}
                  </span>
                </td>
                <td>
                  {(() => {
                    const papelAtual = papeisPorProfessor.get(item.id);
                    if (!papeisPorProfessor.has(item.id)) {
                      // Sem profile = sem login — não há papel pra trocar.
                      return <span className="badge-papel badge-papel-vazio">—</span>;
                    }
                    if (papelEfetivo !== "admin") {
                      return (
                        <span className={`badge-papel badge-papel-${papelAtual}`}>
                          {PAPEL_LABEL[papelAtual] || papelAtual}
                        </span>
                      );
                    }
                    const ehProprioUsuario = item.id === user?.id;
                    return (
                      <select
                        className="select-papel"
                        value={papelAtual}
                        disabled={ehProprioUsuario || salvandoPapelId === item.id}
                        title={
                          ehProprioUsuario
                            ? "Você não pode alterar o próprio papel."
                            : undefined
                        }
                        onChange={(e) => handleTrocarPapel(item, e.target.value)}
                      >
                        {PAPEIS_SELECT.map((papel) => (
                          <option key={papel} value={papel}>
                            {PAPEL_LABEL[papel]}
                          </option>
                        ))}
                      </select>
                    );
                  })()}
                </td>
                <td className="acoes">
                  <button
                    className="btn-acao btn-detalhes"
                    title="Histórico de aulas"
                    onClick={() => handleVerHistorico(item)}
                  >
                    <FontAwesomeIcon icon={faClockRotateLeft} />
                  </button>
                  <button
                    className="btn-acao btn-editar"
                    title="Editar"
                    onClick={() => handleEdit(item)}
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  {PODE_EXCLUIR_PROFESSOR.includes(userRole) && (
                    <button
                      className="btn-acao btn-inativar"
                      title="Excluir"
                      onClick={() => handleDelete(item)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Adicionar event listener para fechar dropdown quando clicar em qualquer lugar */}
      {activeDropdown !== null && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 999,
          }}
          onClick={closeDropdown}
        />
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-professor">
            <div className="modal-header">
              <h2>{modoEdicao ? "Editar Professor" : "Adicionar Professor"}</h2>
              <button className="close-btn" onClick={closeModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="professor-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nome">Nome</label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={novoProfessor.nome}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="email@ironhouse.com"
                    value={novoProfessor.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                {!modoEdicao && (
                  <div className="form-group">
                    <label htmlFor="senha">Senha provisória</label>
                    <input
                      type="password"
                      id="senha"
                      name="senha"
                      placeholder="A pessoa troca no primeiro acesso"
                      value={novoProfessor.senha}
                      onChange={handleChange}
                      required
                      minLength={6}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="idade">Idade (opcional)</label>
                  <input
                    type="number"
                    id="idade"
                    name="idade"
                    value={novoProfessor.idade}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="especialidade">
                    Especialidade (opcional)
                  </label>
                  <input
                    type="text"
                    id="especialidade"
                    name="especialidade"
                    value={novoProfessor.especialidade}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="experiencia">Experiência (opcional)</label>
                  <input
                    type="text"
                    id="experiencia"
                    name="experiencia"
                    value={novoProfessor.experiencia}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="formacao">Formação (opcional)</label>
                <textarea
                  id="formacao"
                  name="formacao"
                  value={novoProfessor.formacao}
                  onChange={handleChange}
                  rows="2"
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancelar"
                  onClick={closeModal}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-salvar">
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de histórico */}
      {professorHistorico && (
        <div className="modal-overlay">
          <div className="modal-professor">
            <div className="modal-header">
              <h2>Histórico de Aulas - {professorHistorico.nome}</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setProfessorHistorico(null);
                  setActiveDropdown(null); // Garantir que dropdown está fechado
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* Filter aulas for the specific professor */}
              {(() => {
                // CORREÇÃO: Filtrar usando aula.professor.id
                const aulasDoProfessor = historicoAulas.filter(
                  (aula) =>
                    aula.professor &&
                    aula.professor.id === professorHistorico.id
                );
                const totalAulasProfessor = aulasDoProfessor.length;
                // Regra §2: contador preciso vem da RPC (agregado no backend);
                // a lista abaixo mostra o histórico recente carregado.
                const aulasRealizadas =
                  aulasRealizadasPorProfessor.get(professorHistorico.id) || 0;

                return (
                  <>
                    <div className="professor-info-resumo">
                      <p>
                        <strong>Idade:</strong> {professorHistorico.idade} anos
                      </p>
                      <p>
                        <strong>Especialidade:</strong>{" "}
                        {professorHistorico.especialidade}
                      </p>
                      <p>
                        <strong>Experiência:</strong>{" "}
                        {professorHistorico.experiencia}
                      </p>
                      <p>
                        <strong>Aulas realizadas:</strong> {aulasRealizadas}
                      </p>
                    </div>

                    {totalAulasProfessor > 0 ? (
                      <table className="tabela-historico">
                        <thead>
                          <tr>
                            <th>Data</th>
                            <th>Status</th>
                            <th>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...aulasDoProfessor] // Use the filtered list
                            .sort((a, b) => {
                              // Ordenar por data (mais recente primeiro)
                              // Assuming data is in DD/MM/YYYY format
                              const dataA = new Date(
                                a.data.split("/").reverse().join("-")
                              );
                              const dataB = new Date(
                                b.data.split("/").reverse().join("-")
                              );
                              // Handle invalid dates if necessary
                              if (isNaN(dataA) || isNaN(dataB)) return 0;
                              return dataB - dataA;
                            })
                            .map((aula) => (
                              <tr key={aula.id}>
                                <td>{aula.data}</td>
                                <td>{getStatusLabel(aula.status)}</td>
                                <td>
                                  <button
                                    className="btn-detalhes"
                                    onClick={() => verDetalhesAula(aula.id)}
                                  >
                                    Ver
                                  </button>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="sem-aulas">
                        Este professor ainda não ministrou nenhuma aula.
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalhes da aula */}
      {showAulaDetalhes && aulaDetalhes && (
        <div className="modal-overlay">
          <div className="modal-professor">
            <div className="modal-header">
              <h2>Detalhes da Aula</h2>
              <button
                className="close-btn"
                onClick={() => {
                  fecharDetalhesAula();
                  setActiveDropdown(null); // Garantir que dropdown está fechado
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="aula-info-detalhes">
                <p>
                  <strong>Data:</strong> {aulaDetalhes.data}
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  {aulaDetalhes.status === "realizada"
                    ? "Aula Realizada"
                    : aulaDetalhes.status === "cancelada"
                    ? "Aula Cancelada"
                    : "Aula Atual"}
                </p>
                <p>
                  <strong>Total de alunos:</strong> {aulaDetalhes.totalAlunos}
                </p>

                <h3>Alunos Presentes:</h3>
                <ul className="lista-alunos-detalhes">
                  {aulaDetalhes.alunos.map((aluno) => (
                    <li key={aluno.id} className="aluno-detalhe-item">
                      <p className="aluno-detalhe-nome">{aluno.nome}</p>
                      <p className="aluno-detalhe-idade">
                        Idade: {aluno.idade}
                      </p>
                    </li>
                  ))}
                </ul>

                {aulaDetalhes.professor && (
                  <>
                    <h3>Professor Responsável:</h3>
                    <div className="professor-detalhe-item">
                      <p className="professor-detalhe-nome">
                        {aulaDetalhes.professor.nome}
                      </p>
                      <p className="professor-detalhe-especialidade">
                        Especialidade: {aulaDetalhes.professor.especialidade}
                      </p>
                    </div>
                  </>
                )}

                {/* Exibir exercícios realizados, se houver */}
                {aulaDetalhes.exercicios &&
                  aulaDetalhes.exercicios.length > 0 && (
                    <>
                      <h3>Exercícios Realizados:</h3>
                      <div className="exercicios-detalhes">
                        {aulaDetalhes.exercicios.map((exercicio) => (
                          <div
                            key={exercicio.id}
                            className="exercicio-detalhe-item"
                          >
                            <p className="exercicio-detalhe-nome">
                              {exercicio.nome}
                            </p>
                            <p className="exercicio-detalhe-musculatura">
                              Musculatura: {exercicio.musculatura}
                            </p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GerenciamentoProfessores;
